/** Duplicated from FpsMinimap inner canvas mapping (160², 4px pad). */
const CANVAS_SIZE = 160;
const PAD = 4;

type PingEntry = { until: number; el: HTMLDivElement };

let pulseStyleInstalled = false;

function ensurePulseKeyframes(): void {
  if (pulseStyleInstalled) return;
  pulseStyleInstalled = true;
  const style = document.createElement('style');
  style.textContent = `@keyframes fpsWorldPingPulse {
  0%, 100% { transform: translate(-50%, -50%) scale(0.85); opacity: 0.75; }
  50% { transform: translate(-50%, -50%) scale(1.25); opacity: 1; }
}`;
  document.head.appendChild(style);
}

/**
 * World-space pings drawn on a 160×160 overlay aligned with the minimap canvas.
 */
export class FpsWorldPingStack {
  private readonly root: HTMLDivElement;
  private readonly worldHalfExtent: number;
  private entries: PingEntry[] = [];

  constructor(mount: HTMLElement, worldHalfExtent: number) {
    ensurePulseKeyframes();
    this.worldHalfExtent = Math.max(1e-6, worldHalfExtent);
    this.root = document.createElement('div');
    this.root.style.cssText = [
      'position:fixed',
      'top:16px',
      'right:16px',
      'width:160px',
      'height:160px',
      'pointer-events:none',
      'z-index:41',
      'overflow:visible',
    ].join(';');
    mount.appendChild(this.root);
  }

  /** Same formula as FpsMinimap.worldToCanvas (private). */
  private worldToLocal(wx: number, wz: number): { cx: number; cy: number } {
    const H = this.worldHalfExtent;
    const nx = (wx + H) / (2 * H);
    const nz = (wz + H) / (2 * H);
    const cx = PAD + nx * (CANVAS_SIZE - 2 * PAD);
    const cy = PAD + (1 - nz) * (CANVAS_SIZE - 2 * PAD);
    return { cx, cy };
  }

  pushPing(wx: number, wz: number, durationMs: number): void {
    const { cx, cy } = this.worldToLocal(wx, wz);
    const el = document.createElement('div');
    el.style.cssText = [
      'position:absolute',
      `left:${cx}px`,
      `top:${cy}px`,
      'width:10px',
      'height:10px',
      'border-radius:50%',
      'background:radial-gradient(circle,#ff6b4a,#ff2e88)',
      'box-shadow:0 0 10px rgba(255,80,120,0.85)',
      'animation:fpsWorldPingPulse 0.85s ease-in-out infinite',
      'transform:translate(-50%,-50%)',
    ].join(';');
    this.root.appendChild(el);
    this.entries.push({ until: performance.now() + durationMs, el });
  }

  /** @param dt Unused; expiry uses performance.now() (keeps API consistent with other game objects). */
  update(_dt: number): void {
    const now = performance.now();
    const alive: PingEntry[] = [];
    for (const e of this.entries) {
      if (now >= e.until) {
        e.el.remove();
      } else {
        alive.push(e);
      }
    }
    this.entries = alive;
  }

  dispose(): void {
    for (const e of this.entries) e.el.remove();
    this.entries = [];
    this.root.remove();
  }
}
